import classNames from 'classnames';
import $ from 'jquery';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';

import * as benchlingUtil from 'BenchlingUtil';
import {isBrowser} from 'lib/Browser';
import * as MediocreConstants from 'notebook/mediocre/MediocreConstants';
import {PaperSize, PrintOrientation} from 'notebook/mediocre/print/PrintConstants';
import PrintOptionsModal from 'notebook/mediocre/print/PrintOptionsModal';
import * as PrintUtil from 'notebook/mediocre/print/PrintUtil';
import {TIMESTAMP_WIDTH} from 'notebook/notes/NoteConstants';
import {loadingIndicatorAttrs, waitForLoadingIndicatorsToResolve} from 'notebook/util/ExportInstrumentation';
import ModalAnchor from 'ui-library/ModalAnchor';
import * as keyboard from 'util/Keyboard';

/**
 * PrintWrapper is a React component that wraps another React component for print:
 *  - Renders a loading screen while there is no component.
 *  - Keeps the loading screen (in browser environment) if a font resource or any <img> resource is
 *    not yet loaded.
 *  - Triggers the print behavior or via Ctrl-P after loading if the current URL's hash is #print
 *    (in browser environment)
 *  - Sizes / orients tables so that they fit-to-width before print
 *
 * The wrapped component may use <div className='printable-page-container'> to introduce linebreaks between
 * logical pages.
 */

interface PrintableWrapperProps {
  title?: string;
  children: React.ReactNode;
  shouldPadForTimestamps?: boolean; // Will add additional padding to the page to accomodate space for timestamps
}

interface State {
  areFileResourcesLoaded: boolean;
  isDOMLoaded: boolean;
  style: JSX.Element | null;
  isOptionsModalOpen: boolean;
  showLinkURLs: boolean;
  printableWidth: number;
}

function isLoadingFile(props: PrintableWrapperProps): boolean {
  return React.Children.count(props.children) === 0;
}

function isLoadingResources(props: PrintableWrapperProps, state: State): boolean {
  return isLoadingFile(props) || !state.areFileResourcesLoaded || !state.isDOMLoaded;
}

const DEFAULT_PAGE_DIMENSIONS_FOR_HEADLESS_CHROME_EXPORTS = PrintUtil.getPageDims(
  PaperSize.US_LETTER,
  PrintOrientation.PORTRAIT
);

class PrintableWrapper extends React.Component<PrintableWrapperProps, State> {
  static propTypes = {
    title: PropTypes.string,
    children: PropTypes.node,
    shouldPadForTimestamps: PropTypes.bool,
  };

  state: State = {
    areFileResourcesLoaded: !isBrowser,
    isDOMLoaded: !isBrowser,
    style: null,
    isOptionsModalOpen: false,
    showLinkURLs: false, // can be overridden by PrintOptionsModal
    printableWidth: 0,
  };

  _isMounted = false;

  componentDidMount(): void {
    this._isMounted = true;
    if (isBrowser) {
      this._waitForInitialDOMLoad();
      benchlingUtil.setWindowTitle(this.props.title);
      document.addEventListener('keydown', this._handleKeyDownForPrint);
      // If the file is loaded on mount, start waiting for the resources to load.
      if (!isLoadingFile(this.props)) {
        this._waitForFileResourcesLoad();
      }
    }
  }

  componentDidUpdate(prevProps: PrintableWrapperProps, prevState: State): void {
    if (isBrowser) {
      // If the title changes (unset to set), set it!
      if (prevProps.title !== this.props.title) {
        benchlingUtil.setWindowTitle(this.props.title);
      }

      // If the file finishes loading, start waiting for the resources to load.
      if (isLoadingFile(prevProps) && !isLoadingFile(this.props)) {
        this._waitForFileResourcesLoad();
      }

      // When the resources finish loading, trigger a print if necessary.
      if (isLoadingResources(prevProps, prevState) && !isLoadingResources(this.props, this.state)) {
        if (window.location.hash === '#print') {
          this._openOptionsModal();
        }
      }
    }
    if (PrintUtil.isHeadlessChrome()) {
      if (isLoadingResources(prevProps, prevState) && !isLoadingResources(this.props, this.state)) {
        // for headless chrome rendering, ensure that fitToWidth styling is applied
        this.setState({
          style: PrintUtil.getFitToWidthStyle(
            ReactDOM.findDOMNode(this) as Element,
            DEFAULT_PAGE_DIMENSIONS_FOR_HEADLESS_CHROME_EXPORTS,
            true
          ),
        });
      }
    }
  }

  componentWillUnmount(): void {
    if (isBrowser) {
      document.removeEventListener('keydown', this._handleKeyDownForPrint);
    }
    this._isMounted = false;
  }

  _waitForInitialDOMLoad(): void {
    if (document.readyState === 'complete') {
      this.setState({isDOMLoaded: true});
    } else {
      window.addEventListener('load', () => {
        if (!this._isMounted) {
          return;
        }
        this.setState({isDOMLoaded: true});
      });
    }
  }

  // TODO (BNCH-90164): add loading indicators to images and rely on waitForLoadingIndicatorsToResolve
  // to detect images load as well
  _waitForImagesToLoad(): Promise<Array<void>> {
    if (!this._isMounted) {
      return Promise.resolve([]);
    }
    const thisNode = ReactDOM.findDOMNode(this) as HTMLElement | null;
    if (!thisNode) {
      return Promise.resolve([]);
    }
    const images = $(thisNode).find('img').toArray();
    return Promise.all(
      images.map(
        (img: HTMLImageElement) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }
            img.addEventListener('load', () => {
              resolve();
            });
            img.addEventListener('error', () => {
              resolve();
            });
          })
      )
    );
  }

  async _waitForFileResourcesLoad(): Promise<void> {
    const thisNode = ReactDOM.findDOMNode(this.refs.content) as HTMLElement | null;
    if (!thisNode) {
      return Promise.resolve();
    }
    await waitForLoadingIndicatorsToResolve(thisNode);
    await this._waitForImagesToLoad();
    if (!this._isMounted) {
      return;
    }
    this.setState({areFileResourcesLoaded: true});
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleKeyDownForPrint = (e: KeyboardEvent) => {
    if (keyboard.isCtrlP(e)) {
      e.preventDefault();
      if (!isLoadingResources(this.props, this.state)) {
        this._openOptionsModal();
      }
    }
  };

  _openOptionsModal(): void {
    this.setState({isOptionsModalOpen: true});
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _closeOptionsModal = () => {
    this.setState({isOptionsModalOpen: false});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _print = ({
    shouldFitToWidth,
    dimensions,
    showLinkURLs,
  }: {
    shouldFitToWidth: boolean;
    dimensions: PrintUtil.PageDims;
    showLinkURLs: boolean;
  }) => {
    let printableWidth = this.props.shouldPadForTimestamps
      ? dimensions.width - TIMESTAMP_WIDTH
      : dimensions.width;
    printableWidth = shouldFitToWidth ? 0 : printableWidth;
    this.setState(
      {
        style: PrintUtil.getFitToWidthStyle(
          ReactDOM.findDOMNode(this) as Element,
          dimensions,
          shouldFitToWidth
        ),
        isOptionsModalOpen: false,
        showLinkURLs,
        printableWidth,
      },
      () => {
        // XXX: We want NoteEditor to rerender to reflect any changes in the width of the printable
        // container. This is required to force CaptionEditables to render the caption location below the
        // captioned content when printing in portrait mode. NoteEditor is memoized but responds to
        // browser window resize events, so we simulate one here.
        window.dispatchEvent(new Event('resize'));
        // Delay opening the OS's print preview modal until after we've had a chance to rerender.
        // XXX: We need to yield 25ms before opening the browser print modal because dismissing
        // the print options modal is debounced, meaning it requires ticks to disappear from the DOM.
        // We arbitrarly set up 25ms since it's not too big of a delay and gives us enough time to close out
        // the modal here.
        setTimeout(() => {
          window.print();
        }, 25);
      }
    );
  };

  render(): JSX.Element {
    const hasResourcesLoading = isLoadingResources(this.props, this.state);
    const hasFileLoading = isLoadingFile(this.props);
    return (
      <div>
        {!hasResourcesLoading ? null : (
          <div className="loading-spinner-background fullscreen-msg" {...loadingIndicatorAttrs()}>
            <div className="loading-text">
              {hasFileLoading ? 'preparing for print...' : 'loading file...'}
            </div>
          </div>
        )}
        <div
          className={classNames('printable-container', {
            'is-loading': hasResourcesLoading,
            'hide-print-links': !this.state.showLinkURLs,
          })}
          data-printable-left-margin={MediocreConstants.MEDIOCRE_PAGE_MARGIN_PX}
          style={this.state.printableWidth > 0 ? {width: `${this.state.printableWidth}px`} : undefined}
        >
          <div className="printable-container-boundingBox" ref="content">
            {!hasFileLoading ? this.props.children : null}
          </div>
        </div>
        {this.state.style}
        <ModalAnchor open={this.state.isOptionsModalOpen}>
          <PrintOptionsModal onRequestClose={this._closeOptionsModal} onRequestPrint={this._print} />
        </ModalAnchor>
      </div>
    );
  }
}

export default PrintableWrapper;
